import React, { useState } from 'react';

export const OpportunitiesPreview = ({ onBack, onSignUp, onApply }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [emailInput, setEmailInput] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const opportunities = [
    {
      id: 1,
      title: 'Share Your Lupus Journey',
      condition: 'Lupus',
      type: 'Survey',
      reward: '$75',
      duration: '30 min',
      description: 'Help researchers understand the daily challenges of living with lupus through a detailed survey about your symptoms and treatments.',
      spots: 12,
      color: '#9b59b6'
    },
    {
      id: 2,
      title: 'Narcolepsy Treatment Experience',
      condition: 'Narcolepsy',
      type: 'Interview',
      reward: '$150',
      duration: '45 min',
      description: 'Participate in a one-on-one interview about your experience with narcolepsy medications and their effectiveness.',
      spots: 8,
      color: '#385cbc'
    },
    {
      id: 3,
      title: 'PV Patient Focus Group',
      condition: 'Polycythemia Vera',
      type: 'Focus Group',
      reward: '$200',
      duration: '90 min',
      description: 'Join other PV patients in a virtual focus group to discuss unmet needs and treatment priorities.',
      spots: 5,
      color: '#f15b41'
    },
    {
      id: 4,
      title: 'IBD Daily Diary Study',
      condition: 'IBD',
      type: 'Diary Study',
      reward: '$300',
      duration: '2 weeks',
      description: 'Track your symptoms and quality of life over two weeks using our simple daily diary app.',
      spots: 20,
      color: '#fab184'
    },
    {
      id: 5,
      title: 'Cancer Survivorship Survey',
      condition: 'Cancer',
      type: 'Survey',
      reward: '$100',
      duration: '45 min',
      description: 'Share your cancer journey and survivorship experience to help improve support programs.',
      spots: 25,
      color: '#385cbc'
    },
    {
      id: 6,
      title: 'Asthma Management Interview',
      condition: 'Asthma',
      type: 'Interview',
      reward: '$125',
      duration: '40 min',
      description: 'Discuss your asthma triggers, treatments, and how you manage your condition day-to-day.',
      spots: 15,
      color: '#819cd1'
    },
    {
      id: 7,
      title: 'Depression Treatment Study',
      condition: 'MDD',
      type: 'Survey + Interview',
      reward: '$175',
      duration: '60 min',
      description: 'Complete a survey and brief interview about your experience with depression treatments.',
      spots: 18,
      color: '#6b8e23'
    },
    {
      id: 8,
      title: 'Allergy Patient Panel',
      condition: 'Allergy',
      type: 'Advisory Board',
      reward: '$500',
      duration: '3 hours',
      description: 'Serve on a patient advisory board helping shape new allergy treatment research priorities.',
      spots: 3,
      color: '#e67e22'
    }
  ];

  const categories = ['all', 'Survey', 'Interview', 'Focus Group', 'Diary Study', 'Advisory Board'];

  const filteredOpportunities = selectedCategory === 'all'
    ? opportunities
    : opportunities.filter(o => o.type.includes(selectedCategory));

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput) {
      setEmailSubmitted(true);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f7f5f3',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 480px) {
          .opps-grid {
            grid-template-columns: 1fr !important;
          }
          .opps-email-section {
            padding: 32px 20px !important;
          }
        }
      `}</style>
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            padding: '10px 20px',
            backgroundColor: '#fff',
            border: '2px solid #272726',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          ← Back
        </button>
      )}

      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(180deg, #d0c7c7 0%, #f7f5f3 100%)',
          padding: '100px 24px 60px',
          textAlign: 'center'
        }}
      >
        <h1
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: '700',
            color: '#272726',
            margin: '0 0 16px 0'
          }}
        >
          Available Opportunities
        </h1>
        <p
          style={{
            fontSize: '18px',
            color: '#555',
            maxWidth: '600px',
            margin: '0 auto 24px',
            lineHeight: '1.6'
          }}
        >
          Browse current research studies looking for patients like you.
          No account needed to explore—sign up when you find something that fits.
        </p>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(56, 92, 188, 0.1)',
            padding: '12px 20px',
            borderRadius: '30px',
            fontSize: '14px',
            color: '#385cbc',
            fontWeight: '600'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
          Updated daily with new opportunities
        </div>
      </div>

      {/* Category filter */}
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '40px',
            justifyContent: 'center'
          }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: '10px 20px',
                borderRadius: '20px',
                border: selectedCategory === cat ? '2px solid #272726' : '2px solid #ddd',
                backgroundColor: selectedCategory === cat ? '#272726' : '#fff',
                color: selectedCategory === cat ? '#fff' : '#555',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {cat === 'all' ? 'All Types' : cat}
            </button>
          ))}
        </div>

        {/* Opportunities grid */}
        <div
          className="opps-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px',
            marginBottom: '60px'
          }}
        >
          {filteredOpportunities.map((opp) => (
            <div
              key={opp.id}
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
              }}
            >
              {/* Color accent bar */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor: opp.color
                }}
              />

              {/* Condition badge */}
              <div
                style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  backgroundColor: `${opp.color}15`,
                  color: opp.color,
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}
              >
                {opp.condition}
              </div>

              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#272726',
                  margin: '0 0 12px 0',
                  lineHeight: '1.3'
                }}
              >
                {opp.title}
              </h3>

              <p
                style={{
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.6',
                  margin: '0 0 20px 0'
                }}
              >
                {opp.description}
              </p>

              {/* Details row */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  marginBottom: '20px',
                  paddingTop: '16px',
                  borderTop: '1px solid #eee'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M12 12h.01"/>
                  </svg>
                  <span style={{ fontSize: '13px', color: '#666' }}>{opp.type}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                  <span style={{ fontSize: '13px', color: '#666' }}>{opp.duration}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                  </svg>
                  <span style={{ fontSize: '13px', color: '#666' }}>{opp.spots} spots left</span>
                </div>
              </div>

              {/* Reward and CTA */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <span style={{ fontSize: '12px', color: '#999', display: 'block' }}>Reward</span>
                  <span
                    style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#272726'
                    }}
                  >
                    {opp.reward}
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (onApply) {
                      onApply({
                        id: `study-${opp.id}`,
                        condition: opp.condition,
                        conditionShort: opp.condition,
                        type: opp.type,
                        title: opp.title,
                        pay: opp.reward,
                        duration: opp.duration,
                        sponsor: `a pharmaceutical company developing ${opp.condition} therapies`,
                        description: opp.description,
                        requirements: [
                          `Diagnosed with ${opp.condition}`,
                          '18 years or older',
                          'US resident'
                        ]
                      });
                    } else if (onSignUp) {
                      onSignUp();
                    }
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: opp.color,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Email capture section */}
        <div
          className="opps-email-section"
          style={{
            backgroundColor: '#272726',
            borderRadius: '20px',
            padding: '48px',
            marginBottom: '60px',
            textAlign: 'center'
          }}
        >
          {!emailSubmitted ? (
            <>
              <h2
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '28px',
                  fontWeight: '600',
                  color: '#fff',
                  margin: '0 0 12px 0'
                }}
              >
                Don't see your condition?
              </h2>
              <p
                style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.7)',
                  margin: '0 0 28px 0',
                  maxWidth: '500px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}
              >
                We're adding new opportunities every week. Leave your email and we'll notify you when studies match your condition.
              </p>
              <form
                onSubmit={handleEmailSubmit}
                style={{
                  display: 'flex',
                  gap: '12px',
                  maxWidth: '450px',
                  margin: '0 auto',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter your email"
                  required
                  style={{
                    flex: '1',
                    minWidth: '200px',
                    padding: '14px 20px',
                    fontSize: '16px',
                    border: 'none',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '14px 28px',
                    backgroundColor: '#f15b41',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d94e36'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f15b41'}
                >
                  Notify Me
                </button>
              </form>
            </>
          ) : (
            <>
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#4CAF50',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#fff',
                  margin: '0 0 8px 0'
                }}
              >
                You're on the list!
              </h2>
              <p
                style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.7)',
                  margin: '0'
                }}
              >
                We'll email you when new opportunities match your interests.
              </p>
            </>
          )}
        </div>

        {/* Stats bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '48px',
            padding: '40px 0 60px',
            borderTop: '1px solid #e5e5e5'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#272726',
                fontFamily: '"Source Serif Pro", Georgia, serif'
              }}
            >
              9
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>Conditions represented</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#272726',
                fontFamily: '"Source Serif Pro", Georgia, serif'
              }}
            >
              $75–$500
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>Per study reward range</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#272726',
                fontFamily: '"Source Serif Pro", Georgia, serif'
              }}
            >
              Weekly
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>New opportunities added</div>
          </div>
        </div>
      </div>
    </div>
  );
};
